import { useState, useEffect, useMemo, useRef } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, Activity, TrendingUp, TrendingDown, Moon, Sun, Volume2, VolumeX, Star } from "lucide-react";
import { Link } from "react-router-dom";
import { StoreHeader } from "../components/StoreHeader";
import { SoundWaveBackground } from "../components/SoundWaveBackground";
import { PricePulseChart } from "../components/PricePulseChart";
import { SpreadChart } from "../components/SpreadChart";
import { AlertButton } from "../components/AlertButton";
import { MetalIndexStrip } from "../components/MetalIndexStrip";
import { LadderBuy } from "../components/LadderBuy";
import { useTickerSound } from "../hooks/useTickerSound";
import { useAuth } from "../context/AuthContext";
import { formatINR } from "../lib/format";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RANGES = [
  { key: 30, label: "30D" },
  { key: 90, label: "90D" },
  { key: 180, label: "6M" },
];

const GROUPS = [
  { key: "all", label: "All Instruments" },
  { key: "headline", label: "Market Indices" },
  { key: "steel", label: "Steel · TMT" },
  { key: "ancillary", label: "Coils · Plates · Structural" },
  { key: "cement", label: "Cement" },
  { key: "metal_index", label: "Metal Index" },
];

// Group-driven glow palette (as requested: cyan-steel, amber-cement)
const groupColor = (group) => {
  switch (group) {
    case "steel": return "#22D3EE";      // cyan
    case "cement": return "#F5A524";     // amber
    case "ancillary": return "#A78BFA";  // violet — for coils/plates/structural
    case "metal_index": return "#C9A961"; // gold — Vinci Metal Index
    case "headline":
    default: return "#B87333";           // copper
  }
};

// Terminal theme: copper (market hours) vs cool sky (after hours). Only the
// primary accent for the hero + filter chips + top movers dot changes; the
// data-encoding group colours (cyan-steel, amber-cement, violet-ancillary)
// remain fixed so glances stay unambiguous.
const THEMES = {
  copper: { primary: "#B87333", label: "Market Hours", icon: Sun },
  cyan: { primary: "#38BDF8", label: "After Hours", icon: Moon },
};

const THEME_KEY = "vinci-pulse-theme";

const brandInitials = (brand) =>
  (brand || "").split(" ").filter(Boolean).slice(0, 2).map((w) => w[0]).join("").toUpperCase() || "◆";

// Small triangular ▲/▼ indicator with a subtle infinite pulse
const PulseTriangle = ({ trend, delta, unit, testid }) => {
  if (!trend || trend === "flat") {
    return (
      <span data-testid={testid} className="inline-flex items-center gap-1 font-grotesk text-[10px] text-[#8A8A82]">
        <span className="inline-block w-2 h-[2px] bg-current" /> 0.00
      </span>
    );
  }
  const isUp = trend === "up";
  const color = isUp ? "#2FB999" : "#FF5A5F";
  return (
    <span
      data-testid={testid}
      className="inline-flex items-center gap-1 font-grotesk text-[11px] font-semibold"
      style={{ color }}
    >
      <svg
        className="pulse-triangle"
        width="10" height="10" viewBox="0 0 10 10"
        style={{ color }}
      >
        {isUp ? (
          <polygon points="5,1 9,9 1,9" fill="currentColor" />
        ) : (
          <polygon points="5,9 9,1 1,1" fill="currentColor" />
        )}
      </svg>
      <span>{isUp ? "+" : "−"}{Math.abs(delta || 0).toFixed(2)}</span>
      {unit && <span className="text-[9px] text-[#8A8A82] font-normal">/ {unit.replace("per ", "")}</span>}
    </span>
  );
};

const InstrumentCard = ({ r, i, alerts, onAlertCreated, starred, onToggleStar }) => {
  const color = groupColor(r.group);
  const rangePct = r.high > r.low ? ((r.price - r.low) / (r.high - r.low)) * 100 : 50;

  return (
    <motion.div
      data-testid={`pulse-card-${r.category}`}
      initial={{ opacity: 0, y: 22 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.55, delay: i * 0.045, ease: [0.16, 1, 0.3, 1] }}
      className="relative border border-white/10 rounded-sm bg-[#08080a]/85 backdrop-blur-sm overflow-hidden group hover:border-white/25 transition-colors"
      style={{ boxShadow: `inset 0 0 0 1px ${color}12` }}
    >
      {/* top accent line */}
      <div className="absolute inset-x-0 top-0 h-[2px]" style={{ background: `linear-gradient(90deg, transparent, ${color}, transparent)`, opacity: 0.55 }} />

      <div className="p-5">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <span
                className="inline-flex items-center justify-center w-7 h-7 rounded-sm font-grotesk text-[10px] font-bold border"
                style={{ color, borderColor: `${color}55`, background: `${color}10` }}
              >
                {brandInitials(r.brand)}
              </span>
              <span className="font-grotesk text-[9px] uppercase tracking-[0.22em]" style={{ color }}>
                {r.brand}
              </span>
              {onToggleStar && (
                <button
                  data-testid={`pulse-star-${r.category}`}
                  onClick={(e) => { e.stopPropagation(); onToggleStar(r.category); }}
                  aria-label={starred ? "Remove from watchlist" : "Add to watchlist"}
                  aria-pressed={!!starred}
                  className={`ml-1 grid place-items-center w-6 h-6 rounded-full transition-colors ${
                    starred ? "text-[#D8B23E]" : "text-[#5f5f58] hover:text-[#D8B23E]"
                  }`}
                >
                  <Star size={13} fill={starred ? "currentColor" : "none"} strokeWidth={2} />
                </button>
              )}
            </div>
            <div className="font-serif text-lg leading-tight text-[#F5F5F0] truncate">{r.label}</div>
          </div>
          <div className="text-right shrink-0">
            <div className="font-serif text-2xl leading-none" style={{ color: "#F5F5F0" }}>
              {formatINR(r.price)}
            </div>
            <div className="font-body text-[9px] text-[#8A8A82] mt-1">{r.unit}</div>
            <div className="mt-1.5 flex justify-end">
              <PulseTriangle trend={r.trend} delta={r.daily_change} unit={r.unit} testid={`pulse-delta-${r.category}`} />
            </div>
          </div>
        </div>

        <div className="mt-4">
          <PricePulseChart series={r.series} color={color} unit={r.unit} brand={r.brand} height={140} targets={alerts} />
        </div>

        <div className="mt-3 flex items-center justify-between gap-3">
          <span className="font-grotesk text-[9px] uppercase tracking-[0.2em] text-[#6f6f68]">
            {(alerts || []).filter((a) => a.status === "active").length > 0
              ? `${(alerts || []).filter((a) => a.status === "active").length} target${(alerts || []).filter((a) => a.status === "active").length > 1 ? "s" : ""} armed`
              : `${(r.series || []).length}D · Terminal feed`}
          </span>
          <AlertButton
            category={r.category}
            label={r.label}
            unit={r.unit}
            price={r.price}
            color={color}
            onCreated={onAlertCreated}
          />
        </div>

        {/* range bar */}
        <div className="mt-4">
          <div className="flex items-center justify-between font-body text-[9px] text-[#6f6f68] font-grotesk uppercase tracking-[0.18em]">
            <span>Low {formatINR(r.low)}</span>
            <span className="tabular-nums" style={{ color }}>
              {r.change_pct > 0 ? "+" : ""}{r.change_pct}% <span className="text-[#8A8A82]">·</span> Range
            </span>
            <span>High {formatINR(r.high)}</span>
          </div>
          <div className="mt-1.5 h-1 rounded-full bg-white/[0.06] relative overflow-hidden">
            <div className="absolute inset-y-0 left-0 rounded-full" style={{ width: "100%", background: `linear-gradient(90deg, ${color}25, ${color}55, ${color}25)` }} />
            <div
              className="absolute top-1/2 -translate-y-1/2 w-2 h-2 rounded-full"
              style={{ left: `calc(${Math.max(0, Math.min(100, rangePct))}% - 4px)`, background: color, boxShadow: `0 0 10px ${color}` }}
            />
          </div>
        </div>

        {/* Ladder Buy — turn live tick into 3 laddered RFQ quantities */}
        <LadderBuy
          category={r.category}
          brand={r.brand}
          label={r.label}
          unit={r.unit}
          price={r.price}
          color={color}
        />
      </div>
    </motion.div>
  );
};

const MarketPulseHero = ({ data, updated, theme }) => {
  const stats = useMemo(() => {
    if (!data.length) return { up: 0, down: 0, flat: 0, avgChange: 0, movers: [] };
    let up = 0, down = 0, flat = 0;
    let totalChange = 0;
    data.forEach((r) => {
      if (r.trend === "up") up++;
      else if (r.trend === "down") down++;
      else flat++;
      totalChange += r.change_pct || 0;
    });
    const movers = [...data].sort((a, b) => Math.abs(b.change_pct) - Math.abs(a.change_pct)).slice(0, 3);
    return { up, down, flat, avgChange: totalChange / data.length, movers };
  }, [data]);

  const steelSeries = useMemo(
    () => data.find((r) => r.category === "tmt")?.series || [],
    [data]
  );
  const cementSeries = useMemo(
    () => data.find((r) => r.category === "cement")?.series || [],
    [data]
  );

  const heroSentiment = stats.up > stats.down ? "up" : stats.down > stats.up ? "down" : "flat";
  const heroColor = heroSentiment === "up" ? "#2FB999" : heroSentiment === "down" ? "#FF5A5F" : theme.primary;

  return (
    <div
      data-testid="pulse-hero"
      className="relative border border-white/10 rounded-sm bg-gradient-to-br from-[#0a0a0c] via-[#0b0b0e] to-[#08080a] p-6 md:p-8 overflow-hidden"
      style={{ boxShadow: `inset 0 0 60px ${heroColor}0d` }}
    >
      {/* Animated status stripe */}
      <div className="absolute top-0 inset-x-0 h-[2px] overflow-hidden">
        <motion.div
          className="h-full"
          style={{ background: `linear-gradient(90deg, transparent, ${heroColor}, transparent)`, width: "40%" }}
          animate={{ x: ["-40%", "260%"] }}
          transition={{ duration: 4.5, ease: "linear", repeat: Infinity }}
        />
      </div>

      <div className="grid md:grid-cols-[1.5fr_1fr] gap-6 items-end">
        <div>
          <div className="inline-flex items-center gap-2 font-grotesk text-[10px] uppercase tracking-[0.28em]" style={{ color: theme.primary }}>
            <span className="inline-block w-1.5 h-1.5 rounded-full pulse-dot" style={{ background: heroColor, color: heroColor }} />
            Live · Market Pulse
          </div>
          <h1 className="font-serif text-4xl md:text-6xl mt-3 tracking-[-0.02em] ticker-glow" style={{ color: heroColor }}>
            {stats.avgChange >= 0 ? "+" : ""}{stats.avgChange.toFixed(2)}%
          </h1>
          <p className="font-body text-sm text-[#8A8A82] mt-3 max-w-lg leading-relaxed">
            Aggregate movement across {data.length} steel, cement &amp; ancillary instruments tracked by the Vinci trade desk.
          </p>
          <div className="flex flex-wrap gap-4 mt-5 font-grotesk text-[10px] uppercase tracking-[0.2em]">
            <span className="inline-flex items-center gap-1.5 text-[#2FB999]" data-testid="pulse-count-up">
              <TrendingUp size={12} /> {stats.up} Advancing
            </span>
            <span className="inline-flex items-center gap-1.5 text-[#FF5A5F]" data-testid="pulse-count-down">
              <TrendingDown size={12} /> {stats.down} Declining
            </span>
            <span className="inline-flex items-center gap-1.5 text-[#8A8A82]">
              <Activity size={12} /> {stats.flat} Unchanged
            </span>
            {updated && <span className="text-[#6f6f68]">Updated {new Date(updated).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })} IST</span>}
          </div>

          {/* Steel vs Cement spread overlay */}
          {(steelSeries.length > 1 || cementSeries.length > 1) && (
            <div className="mt-6 pt-5 border-t border-white/[0.08]">
              <SpreadChart steelSeries={steelSeries} cementSeries={cementSeries} height={72} />
            </div>
          )}
        </div>

        <div className="border-l border-white/10 md:pl-6">
          <div className="font-grotesk text-[9px] uppercase tracking-[0.28em] text-[#8A8A82] mb-3">Top movers · Session</div>
          <ul className="space-y-2">
            {stats.movers.map((m) => {
              const c = groupColor(m.group);
              return (
                <li key={m.category} className="flex items-center justify-between gap-3">
                  <span className="flex items-center gap-2 min-w-0">
                    <span className="inline-block w-1.5 h-1.5 rounded-full" style={{ background: c }} />
                    <span className="font-body text-xs text-[#C9C9C2] truncate">{m.label}</span>
                  </span>
                  <PulseTriangle trend={m.trend} delta={m.daily_change} testid={`mover-${m.category}`} />
                </li>
              );
            })}
          </ul>
        </div>
      </div>
    </div>
  );
};

export default function MarketTrends() {
  return (
    <>
      <PricePulseGridImport />
      <OriginalMarketTrends />
    </>
  );
}

function PricePulseGridImport() {
  const Grid = require("../components/PricePulseGrid").default;
  return <Grid />;
}

function OriginalMarketTrends() {
  const [range, setRange] = useState(90);
  const [group, setGroup] = useState("all");
  const [themeKey, setThemeKey] = useState(() => {
    try {
      return localStorage.getItem(THEME_KEY) || "copper";
    } catch { return "copper"; }
  });
  const theme = THEMES[themeKey] || THEMES.copper;
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [alertsByCategory, setAlertsByCategory] = useState({});
  const [alertsVersion, setAlertsVersion] = useState(0);
  const [heartbeat, setHeartbeat] = useState(null);
  const { user } = useAuth();
  const sound = useTickerSound();
  const prevAvgRef = useRef(null);
  const [watchlist, setWatchlist] = useState([]);

  const toggleStar = async (category) => {
    if (!user) {
      // Not authenticated — silently ignore; the star only exists as a
      // hover affordance for authenticated buyers/sellers.
      return;
    }
    // Optimistic toggle
    setWatchlist((prev) => (
      prev.includes(category) ? prev.filter((c) => c !== category) : [...prev, category]
    ));
    try {
      const r = await axios.post(`${API}/trends/watchlist/${category}`, {}, { withCredentials: true });
      setWatchlist(r.data?.starred || []);
    } catch {
      // Revert on failure
      setWatchlist((prev) => (
        prev.includes(category) ? prev.filter((c) => c !== category) : [...prev, category]
      ));
    }
  };

  useEffect(() => {
    if (!user) { setWatchlist([]); return; }
    axios.get(`${API}/trends/watchlist`, { withCredentials: true })
      .then((r) => setWatchlist(r.data?.starred || []))
      .catch(() => setWatchlist([]));
  }, [user]);

  const toggleTheme = () => {
    const next = themeKey === "copper" ? "cyan" : "copper";
    setThemeKey(next);
    try { localStorage.setItem(THEME_KEY, next); } catch {}
  };

  useEffect(() => {
    setLoading(true);
    axios
      .get(`${API}/rates/history?days=${range}`)
      .then((r) => setData(r.data || []))
      .catch(() => setData([]))
      .finally(() => setLoading(false));
  }, [range]);

  // Lightweight "heartbeat": poll /api/rates every 12s.  If any instrument's
  // price changed, splice it into the current history-driven data set, refresh
  // the hero avg-change and — if the ticker sound is on — click.
  useEffect(() => {
    let alive = true;
    const tick = async () => {
      try {
        const r = await axios.get(`${API}/rates`);
        if (!alive) return;
        setHeartbeat(new Date().toISOString());
        setData((prev) => {
          if (!prev.length) return prev;
          const byCat = new Map(r.data.map((x) => [x.category, x]));
          let changed = false;
          const next = prev.map((row) => {
            const live = byCat.get(row.category);
            if (!live) return row;
            if (Math.abs((live.price || 0) - (row.price || 0)) < 0.01) return row;
            changed = true;
            return {
              ...row,
              price: live.price,
              prev_price: live.prev_price,
              daily_change: live.daily_change,
              change_pct: live.change_pct,
              trend: live.trend,
            };
          });
          if (changed && sound.enabled) {
            const upCount = next.filter((x) => x.trend === "up").length;
            const downCount = next.filter((x) => x.trend === "down").length;
            sound.click(upCount >= downCount ? "up" : "down");
          }
          return changed ? next : prev;
        });
      } catch {}
    };
    const id = setInterval(tick, 12000);
    return () => { alive = false; clearInterval(id); };
  }, [sound]);

  // Fire a subtle click every time the aggregate hero % moves.
  const avgChange = useMemo(() => {
    if (!data.length) return null;
    const t = data.reduce((acc, r) => acc + (r.change_pct || 0), 0) / data.length;
    return Math.round(t * 100) / 100;
  }, [data]);

  useEffect(() => {
    if (avgChange == null) return;
    if (prevAvgRef.current == null) { prevAvgRef.current = avgChange; return; }
    if (Math.abs(avgChange - prevAvgRef.current) > 0.001) {
      sound.click(avgChange > prevAvgRef.current ? "up" : "down");
      prevAvgRef.current = avgChange;
    }
  }, [avgChange, sound]);

  // Load partner alerts once logged-in — used for the stacked target markers
  // on each instrument chart, and to refresh after a new alert is created.
  useEffect(() => {
    if (!user) { setAlertsByCategory({}); return; }
    let alive = true;
    axios.get(`${API}/alerts`, { withCredentials: true }).then((r) => {
      if (!alive) return;
      const map = {};
      for (const a of (r.data || [])) {
        (map[a.category] = map[a.category] || []).push(a);
      }
      setAlertsByCategory(map);
    }).catch(() => {});
    return () => { alive = false; };
  }, [user, alertsVersion]);

  const filtered = useMemo(() => {
    const order = { headline: 0, steel: 1, ancillary: 2, cement: 3 };
    const list = group === "all" ? data : data.filter((r) => (r.group || "headline") === group);
    const isStarred = (r) => watchlist.includes((r.category || "").toLowerCase());
    return [...list].sort((a, b) => {
      // Starred instruments float to the top of the grid.
      const sa = isStarred(a) ? 0 : 1;
      const sb = isStarred(b) ? 0 : 1;
      if (sa !== sb) return sa - sb;
      const ga = order[a.group ?? "headline"] ?? 9;
      const gb = order[b.group ?? "headline"] ?? 9;
      if (ga !== gb) return ga - gb;
      return (a.label || "").localeCompare(b.label || "");
    });
  }, [data, group, watchlist]);

  const updated = useMemo(() => {
    if (!data.length) return null;
    return new Date().toISOString();
  }, [data]);

  return (
    <div className="min-h-screen bg-[#050506] text-[#F5F5F0] relative overflow-hidden">
      <StoreHeader />

      <section className="relative">
        <SoundWaveBackground />

        <div className="relative max-w-[1500px] mx-auto px-6 md:px-12 pt-12 pb-24">
          <Link
            to="/shop"
            className="inline-flex items-center gap-2 font-grotesk text-[10px] uppercase tracking-[0.24em] text-[#8A8A82] hover:text-[#B87333] transition-colors"
          >
            <ArrowLeft size={13} /> Back to store
          </Link>

          <div className="mt-6 mb-8">
            <MarketPulseHero data={data} updated={updated} theme={theme} />
          </div>

          <MetalIndexStrip alertsByCategory={alertsByCategory} onAlertCreated={() => setAlertsVersion((v) => v + 1)} />

          {/* Controls row */}
          <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
            <div className="flex flex-wrap items-center gap-1.5 rounded-full border border-white/12 p-1 bg-black/40 backdrop-blur-md" data-testid="pulse-group-filter">
              {GROUPS.map((g) => (
                <button
                  key={g.key}
                  data-testid={`pulse-group-${g.key}`}
                  onClick={() => setGroup(g.key)}
                  className={`px-4 py-1.5 rounded-full font-grotesk text-[10px] uppercase tracking-[0.18em] transition-colors ${
                    group === g.key ? "text-black" : "text-[#B8B8B0] hover:text-[#F5F5F0]"
                  }`}
                  style={group === g.key ? { background: theme.primary } : undefined}
                >
                  {g.label}
                </button>
              ))}
            </div>

            <div className="flex items-center gap-2">
              {/* Sound toggle */}
              <button
                type="button"
                onClick={sound.toggle}
                data-testid="pulse-sound-toggle"
                className="inline-flex items-center gap-2 rounded-full border border-white/15 px-3 py-1.5 bg-black/40 backdrop-blur-md hover:border-white/35 transition-colors"
                title={sound.enabled ? "Mute live ticker" : "Enable live ticker sound"}
                aria-pressed={sound.enabled}
              >
                {sound.enabled
                  ? <Volume2 size={12} style={{ color: theme.primary }} />
                  : <VolumeX size={12} className="text-[#8A8A82]" />}
                <span
                  className="font-grotesk text-[10px] uppercase tracking-[0.18em]"
                  style={{ color: sound.enabled ? theme.primary : "#8A8A82" }}
                >
                  {sound.enabled ? "Ticker On" : "Ticker Off"}
                </span>
              </button>

              {/* Theme toggle */}
              <button
                type="button"
                onClick={toggleTheme}
                data-testid="pulse-theme-toggle"
                className="inline-flex items-center gap-2 rounded-full border border-white/15 px-3 py-1.5 bg-black/40 backdrop-blur-md hover:border-white/35 transition-colors group"
                title={`Switch to ${themeKey === "copper" ? "After Hours" : "Market Hours"} theme`}
              >
                {(() => { const ThemeIcon = theme.icon; return <ThemeIcon size={12} style={{ color: theme.primary }} />; })()}
                <span className="font-grotesk text-[10px] uppercase tracking-[0.18em]" style={{ color: theme.primary }}>
                  {theme.label}
                </span>
              </button>

              <div className="flex items-center gap-1.5 rounded-full border border-white/12 p-1 bg-black/40 backdrop-blur-md" data-testid="pulse-range-filter">
                {RANGES.map((r) => (
                  <button
                    key={r.key}
                    data-testid={`pulse-range-${r.key}`}
                    onClick={() => setRange(r.key)}
                    className={`px-4 py-1.5 rounded-full font-grotesk text-[10px] uppercase tracking-[0.18em] transition-colors ${
                      range === r.key ? "text-black" : "text-[#B8B8B0] hover:text-[#F5F5F0]"
                    }`}
                    style={range === r.key ? { background: theme.primary } : undefined}
                  >
                    {r.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {loading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="pulse-loading">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="h-72 rounded-sm border border-white/10 bg-[#0a0a0c]/60 animate-pulse" />
              ))}
            </div>
          ) : (
            <AnimatePresence mode="wait">
              <motion.div
                key={`${group}-${range}`}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3 }}
                className="grid md:grid-cols-2 lg:grid-cols-3 gap-5"
                data-testid="pulse-grid"
              >
                {filtered.map((r, i) => (
                  <InstrumentCard
                    key={r.category}
                    r={r}
                    i={i}
                    alerts={alertsByCategory[r.category] || []}
                    onAlertCreated={() => setAlertsVersion((v) => v + 1)}
                    starred={watchlist.includes((r.category || "").toLowerCase())}
                    onToggleStar={user ? toggleStar : null}
                  />
                ))}
                {filtered.length === 0 && (
                  <div className="col-span-full text-center py-16 font-body text-[#8A8A82]">
                    No instruments in this bucket yet.
                  </div>
                )}
              </motion.div>
            </AnimatePresence>
          )}

          <p className="font-body text-[10px] text-[#6f6f68] mt-8 max-w-2xl">
            Indicative rates aggregated from Vinci trade desk. Final price is confirmed at checkout against live stock, freight lane and your partner tier.
          </p>
        </div>
      </section>
    </div>
  );
}
